/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback } from "react";
import { isActivationKey } from "./activationKeys";

/**
 * Props that make a non-`<button>` behave like a button: `role`, `tabIndex`,
 * `aria-disabled`, optional `aria-pressed`, and Enter/Space → `onClick`.
 *
 * - Returns `{}` without `onClick` (stays a plain element).
 * - Activates only on the element's own focus, ignoring bubbled child events.
 * - Pass `selected` for toggles only (adds `aria-pressed`).
 */
export const useClickableA11y = ({ onClick, disabled = false, selected } = {}) => {
  const isInteractive = Boolean(onClick);
  const isActivatable = isInteractive && !disabled;

  const handleKeyDown = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;
      if (isActivationKey(event)) {
        event.preventDefault();
        isActivatable && onClick(event);
      }
    },
    [isActivatable, onClick]
  );

  if (!isInteractive) return {};

  return {
    role: "button",
    tabIndex: disabled ? -1 : 0,
    "aria-disabled": disabled,
    "aria-pressed": selected !== undefined ? Boolean(selected) : undefined,
    onKeyDown: handleKeyDown,
  };
};
